import type { Row } from '@tanstack/react-table';
import { FC, useCallback, useMemo, useRef } from 'react';
import { InlineNotification } from '@carbon/react';
import { DataTable, type DataTableSelectionState } from 'components/data-table';
import { type DcaWorkspaceWellSelectedJob } from 'types/dca-workspace-wells-api/selected-job';
import {
  wellInformationColumns,
  type DcaWorkspaceWellTableRow,
} from './well-information-columns';
import { jobLabel } from './well-jobs-columns';
import type { DcaWorkspaceWellsHook } from './hooks';
import { type WellSelectionState, isWellSelected } from './atoms';
import { WellJobsSubTable } from './well-jobs-sub-table';
import {
  WellSelectionFilter,
  type WellSelectionFilterMode,
} from './well-selection-filter';
import styles from './well-information-section.module.scss';

/**
 * Header checkbox click — `{ selectAll, toggledNodes: [] }`.
 * Per-row clicks always carry at least one toggled node.
 *
 * @param {DataTableSelectionState} selection - DataTable selection payload.
 * @return {boolean} True if the header bulk checkbox was clicked.
 */
export function isHeaderBulkSelection(
  selection: DataTableSelectionState
): boolean {
  return selection.toggledNodes.length === 0;
}
/**
 * Per-row selection delta for wells on the current table page.
 */
export type WellTableSelectionDelta = {
  selected: string[];
  deselected: string[];
};

/**
 * Encode the per-well selection state from `wellsMap` into the AG Grid–style
 * `{ selectAll, toggledNodes }` shape that DataTable's selection column
 * expects, *re-scoped to the visible page*.
 *
 * Picking the encoding lets DataTable's built-in header render the correct
 * state with zero changes to DataTable itself:
 *   - all visible selected  → header `checked`
 *   - none visible selected → header `unchecked`
 *   - mixed                 → header `indeterminate` (rows still resolve
 *                              because per-row cells check
 *                              `toggledNodes.has(row.id)` when
 *                              `selectAll === false`).
 *
 * Exported for direct unit testing.
 *
 * @param {string[]} visibleUwis - UWIs currently rendered in the table.
 * @param {Function} isSelected - Predicate against `wellsMap` (uwi => boolean).
 * @return {DataTableSelectionState} Selection state in DataTable's contract.
 */
export function encodeVisibleSelection(
  visibleUwis: string[],
  isSelected: (uwi: string) => boolean
): DataTableSelectionState {
  const selected = visibleUwis.filter(isSelected);
  if (visibleUwis.length > 0 && selected.length === visibleUwis.length) {
    return { selectAll: true, toggledNodes: [] };
  }
  if (selected.length === 0) {
    return { selectAll: false, toggledNodes: [] };
  }
  return { selectAll: false, toggledNodes: selected };
}

/**
 * Translate the AG-style payload DataTable emits via `onRowSelect` into a
 * delta scoped to the visible page. Per-row clicks fire `{ selectAll,
 * toggledNodes (post-toggle) }`. Header bulk clicks are handled separately
 * via `onBulkSelectionChange`.
 *
 * Exported for direct unit testing.
 *
 * @param {DataTableSelectionState} selection - DataTable's AG-style payload.
 * @param {string[]} visibleUwis - UWIs currently rendered in the table.
 * @return {WellTableSelectionDelta} Per-row delta over the visible scope.
 */
export function decodeVisibleSelection(
  selection: DataTableSelectionState,
  visibleUwis: string[]
): WellTableSelectionDelta {
  const referenced = new Set(selection.toggledNodes);
  const selected: string[] = [];
  const deselected: string[] = [];
  for (const uwi of visibleUwis) {
    const isSelected = selection.selectAll
      ? !referenced.has(uwi) // selectAll + exceptions
      : referenced.has(uwi); // explicit selection list
    if (isSelected) selected.push(uwi);
    else deselected.push(uwi);
  }
  return { selected, deselected };
}

type Props = {
  workspaceId: number;
  wellsHook: DcaWorkspaceWellsHook;
  wellsMap: WellSelectionState;
  onSelectionChange: (delta: WellTableSelectionDelta) => void;
  onBulkSelectionChange: (selectAll: boolean) => void;
  highlightedRowIds?: Set<string>;
  selectionFilterMode: WellSelectionFilterMode;
  onSelectionFilterModeChange: (mode: WellSelectionFilterMode) => void;
};

export const WellInformationSection: FC<Props> = ({
  workspaceId,
  wellsHook,
  wellsMap,
  onSelectionChange,
  onBulkSelectionChange,
  highlightedRowIds,
  selectionFilterMode,
  onSelectionFilterModeChange,
}) => {
  const {
    wells,
    meta,
    loading,
    error,
    onFilterChange: hookOnFilterChange,
    onGlobalSearchChange: hookOnGlobalSearchChange,
    onPageChange: hookOnPageChange,
  } = wellsHook;

  const hadDataRef = useRef(false);
  if (wells.length > 0) hadDataRef.current = true;
  const isInitialLoad = loading && !hadDataRef.current;
  const isRefetching = loading && hadDataRef.current;

  const tableData = useMemo<DcaWorkspaceWellTableRow[]>(() => {
    const rows = wells.map((well) => {
      const forecastId = wellsMap.items[well.uwi]?.dcaForecastId ?? '';
      let selectedJob: DcaWorkspaceWellSelectedJob | null = null;

      if (forecastId) {
        const match = well.jobs?.find(
          (j) => String(j.wellDcaForecastId ?? '') === forecastId
        );
        if (match) {
          selectedJob = {
            ...match,
            rowId: `${match.dcaWellRegressionId}:${match.source}:${match.jobId}`,
            jobIdDisplay: jobLabel(match.jobId),
            typeCurveDisplay: well.typeCurve ?? '',
          };
        }
      }

      return { ...well, selectedJob };
    });

    if (selectionFilterMode === 'selected') {
      return rows.filter((r) => isWellSelected(wellsMap, r.uwi));
    }
    if (selectionFilterMode === 'not_selected') {
      return rows.filter((r) => !isWellSelected(wellsMap, r.uwi));
    }
    return rows;
  }, [wells, wellsMap, selectionFilterMode]);

  const visibleUwis = useMemo(() => tableData.map((r) => r.uwi), [tableData]);

  const selectionInitial = useMemo(
    () =>
      encodeVisibleSelection(visibleUwis, (uwi) =>
        isWellSelected(wellsMap, uwi)
      ),
    [visibleUwis, wellsMap]
  );

  const handleDataTableSelectionChange = useCallback(
    (selection: DataTableSelectionState) => {
      if (isHeaderBulkSelection(selection)) {
        onBulkSelectionChange(selection.selectAll);
        return;
      }
      onSelectionChange(decodeVisibleSelection(selection, visibleUwis));
    },
    [visibleUwis, onSelectionChange, onBulkSelectionChange]
  );

  const renderSub = useCallback(
    (row: Row<DcaWorkspaceWellTableRow>) => (
      <WellJobsSubTable
        workspaceId={workspaceId}
        uwi={row.original.uwi}
        jobs={row.original.jobs ?? []}
        typeCurveFallback={row.original.typeCurve}
        typeCurveHref={row.original.resources?.typeCurve?.href ?? null}
      />
    ),
    [workspaceId]
  );

  return (
    <>
      {error && <InlineNotification kind="error" title={error} lowContrast />}
      <div className={styles.tableContainer}>
        {isRefetching && <div className={styles.loadingOverlay} />}
        <DataTable<DcaWorkspaceWellTableRow & Record<string, unknown>>
          data={
            tableData as (DcaWorkspaceWellTableRow & Record<string, unknown>)[]
          }
          columns={wellInformationColumns}
          getRowId={(row) => String(row.uwi)}
          isLoading={isInitialLoad}
          persistenceKey={`dca-workspace-wells-${workspaceId}`}
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount: meta.total,
          }}
          onPaginationChange={(s) => hookOnPageChange(s.page, s.pageSize)}
          onFilterChange={(s) => hookOnFilterChange(s.columnFilters)}
          onGlobalSearchChange={(s) => hookOnGlobalSearchChange(s.globalFilter)}
          onSelectionChange={(s) => handleDataTableSelectionChange(s.selection)}
          highlightedRowIds={highlightedRowIds}
          // Controlled selection: `wellsMap` (the atom-backed selection) is the source of
          // truth, encoded onto the visible rows; user checkbox changes report via
          // `onSelectionChange` → decode → the atom. Always controlled — independent of
          // `selectionFilterMode`, which only toggles whether checkboxes render.
          controlledState={{ selection: selectionInitial }}
          renderRowSubComponent={(row) =>
            renderSub(row as Row<DcaWorkspaceWellTableRow>)
          }
          searchPlaceholder="Search wells…"
          config={{
            sorting: false,
            hideFooter: true,
            selectionType: selectionFilterMode === 'all' ? 'multi' : undefined,
            export: false,
            globalSearch: true,
            columnFilters: true,
          }}
          leftToolbarActions={[
            <WellSelectionFilter
              key="well-selection-filter"
              appliedMode={selectionFilterMode}
              onApply={onSelectionFilterModeChange}
            />,
          ]}
        />
      </div>
    </>
  );
};
